import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Lock } from "lucide-react";
import { Button } from "../ui/button";
import { computeProjectionTeaser } from "../../lib/projectionTeaser";
import { API } from "../../lib/helpers";

const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

// Fire impression event once per session per (property_id) to avoid
// double-firing under React StrictMode and grid re-renders.
const SESSION_KEY = "saved_card_projection_teaser_seen";
const seenInSession = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]")); }
  catch { return new Set(); }
};
const markSeen = (id) => {
  try {
    const s = seenInSession();
    s.add(String(id));
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(s)));
  } catch { /* best-effort: storage unavailable */ }
};

const trackEvent = (eventType, metadata) => {
  try {
    axios.post(`${API}/analytics/track`, { event_type: eventType, metadata }).catch(() => {});
  } catch { /* best-effort analytics */ }
};

/**
 * Subtle Elite-tier teaser shown UNDER each saved-property card for Pro users.
 * Hidden for Free and Elite. Self-hides if projection numbers would be scary.
 */
export const SavedPropertyEliteTeaser = ({ subscription, property, analysis }) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [hasFiredImpression, setHasFiredImpression] = useState(false);

  // Pro-only gate. Free & Elite hide entirely.
  const isPro = !!subscription?.is_pro;
  const isElite = !!subscription?.is_elite;
  const isProOnly = isPro && !isElite;
  const teaser = isProOnly ? computeProjectionTeaser(analysis) : null;

  const propertyId = property?.id;

  // Coverage tracking — fire ONE evaluation event per property per session
  // for Pro-only users. Records whether the teaser actually shows or hides
  // and why, so admin funnel can compute "% of saved properties showing teaser".
  useEffect(() => {
    if (!isProOnly || !propertyId) return;
    const evalKey = `saved_card_projection_teaser_evaluated_${propertyId}`;
    try {
      if (sessionStorage.getItem(evalKey)) return;
      sessionStorage.setItem(evalKey, "1");
    } catch { /* best-effort: storage unavailable */ }
    let reason = "shown";
    if (!analysis) reason = "no_analysis";
    else if (!teaser) {
      const pp = Number(analysis.purchase_price) || 0;
      const ic = Number(analysis.total_capital_needed) || 0;
      const mr = Number(analysis.estimated_monthly_rent) || 0;
      if (pp <= 0 || ic <= 0 || mr <= 0) reason = "incomplete_analysis";
      else reason = "negative_or_low_return";
    }
    trackEvent("saved_card_projection_teaser_evaluated", {
      property_id: propertyId,
      shown: !!teaser,
      reason,
    });
  }, [isProOnly, propertyId, analysis, teaser]);

  // IntersectionObserver — fire impression only when card is actually visible.
  useEffect(() => {
    if (!teaser || !propertyId || hasFiredImpression) return;
    const seen = seenInSession();
    if (seen.has(String(propertyId))) {
      setHasFiredImpression(true);
      return;
    }
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          markSeen(propertyId);
          trackEvent("saved_card_projection_teaser_viewed", {
            property_id: propertyId,
            irr_pct: teaser.irr_pct,
            equity_multiple: teaser.equity_multiple,
          });
          setHasFiredImpression(true);
          obs.disconnect();
        }
      });
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [teaser, propertyId, hasFiredImpression]);

  if (!teaser) return null;

  const handleClick = (e) => {
    e.stopPropagation();
    trackEvent("saved_card_projection_teaser_clicked", {
      property_id: propertyId,
      irr_pct: teaser.irr_pct,
      equity_multiple: teaser.equity_multiple,
    });
    navigate("/billing?tier=elite_monthly&src=saved_card_teaser");
  };

  return (
    <div
      ref={cardRef}
      data-testid={`saved-elite-teaser-${propertyId}`}
      className="mt-2 rounded-lg border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.04] to-transparent px-3 py-2.5"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <Sparkles className="w-3 h-3 text-amber-500 flex-shrink-0" />
          <p className="text-[11px] font-medium text-foreground truncate">
            Unlock the 10-year projection for this deal
          </p>
        </div>
        <Button
          size="sm"
          onClick={handleClick}
          data-testid={`saved-elite-teaser-cta-${propertyId}`}
          className="h-6 px-2 text-[10px] bg-amber-500 hover:bg-amber-600 text-black font-semibold flex items-center gap-1 flex-shrink-0"
        >
          <Lock className="w-2.5 h-2.5" />
          Unlock Elite
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <div className="rounded bg-secondary/40 px-2 py-1 text-center">
          <p className="text-[8px] uppercase tracking-wider text-muted-foreground/80">10-yr wealth</p>
          <p className="font-mono text-xs font-bold blur-sm select-none" data-testid="teaser-wealth">
            {fmtMoney(teaser.total_return)}
          </p>
        </div>
        <div className="rounded bg-secondary/40 px-2 py-1 text-center">
          <p className="text-[8px] uppercase tracking-wider text-muted-foreground/80">IRR</p>
          <p className="font-mono text-xs font-bold blur-sm select-none" data-testid="teaser-irr">
            {teaser.irr_pct.toFixed(1)}%
          </p>
        </div>
        <div className="rounded bg-secondary/40 px-2 py-1 text-center">
          <p className="text-[8px] uppercase tracking-wider text-muted-foreground/80">Eq mult</p>
          <p className="font-mono text-xs font-bold blur-sm select-none" data-testid="teaser-eqm">
            {teaser.equity_multiple}x
          </p>
        </div>
      </div>
      <p className="text-[8px] text-muted-foreground/70 mt-1.5">
        Assumes {teaser.assumptions.rent_growth_pct}% rent growth · {teaser.assumptions.appreciation_pct}% appreciation · {teaser.assumptions.hold_years}-year hold
      </p>
    </div>
  );
};

export default SavedPropertyEliteTeaser;
